import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, RotateCcw, Home, ChevronRight, Volume2, VolumeX, Sparkles } from 'lucide-react';
import { Congrats, useLoungeCountdown, LoungeCountdown } from '@/lib/loungeGame';
import {
  recordRun, playSfx, isSfxMuted, setSfxMuted, subscribe, getMeta,
  getFD, saveFDLevelResult, setFDLastLevel, saveFDMaster,
} from '@/lib/loungeMeta';
import {
  FD_LEVELS, FD_MAX_LEVEL, MASTER_UNLOCK_LEVEL, worldForLevel,
  getFDLevelConfig, computeFDStars, GARAGE_SKINS, GARAGE_TRAILS, skinById, trailById,
} from '@/lib/focusDriveLevels';
import FocusDriveLevelSelect from '@/components/lounge/FocusDriveLevelSelect';
import FocusDriveGarage from '@/components/lounge/FocusDriveGarage';
import StarBadge from '@/components/lounge/StarBadge';

/**
 * Neon Drift — a premium vertical endless focus racer.
 *
 * Portrait canvas. 3 lanes. Road scrolls downward. The car sits at the bottom
 * and the world flies "up" toward the player. Dodge red barriers, collect
 * gold Brotherhood cubes. Distance in metres = timer. Best distance persisted
 * in localStorage. Difficulty (Easy / Normal / Hard) tunes speed, obstacle
 * density, and collectible generosity.
 *
 * Controls
 *   • Desktop: ← / → arrows, A / D, or click left/right of the car
 *   • Mobile:  swipe left / right or tap left/right of the car
 *   • Space or top-right button: Pause / Resume
 *
 * The design goal is FLOW, not stress. Motion is smooth (60fps rAF), lane
 * changes are eased with a spring, and the palette stays cohesive with the
 * rest of the Lounge (deep navy + warm gold).
 */

const BEST_KEY = 'focus_drive_best';
const TOKENS_KEY = 'focus_drive_tokens';
const DATES_KEY = 'focus_drive_dates';

const LANE_COUNT = 3;

// Every level's diff config comes from focusDriveLevels.js now (100 levels).
// This shim maps the campaign config keys to the field names the physics loop expects.
const diffConfig = (lv) => {
  const cfg = getFDLevelConfig(lv);
  return {
    baseSpeed: cfg.baseSpeed,
    maxSpeed:  cfg.maxSpeed,
    obsRate:   cfg.obsRate,
    tokRate:   cfg.tokRate,
    ramp:      cfg.ramp,
    curveAmp:  cfg.curveAmp,
    curveFreq: cfg.curveFreq,
    doubleObsProb: cfg.doubleObsProb,
    tokenMax:  cfg.tokenMax,
    duration:  cfg.duration,
  };
};

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const rand = (a, b) => a + Math.random() * (b - a);

/* ----- Colours ----- */
const NAVY = '#0B192C';
const NAVY_2 = '#0A0F1C';
const GOLD = '#D4AF37';
const GOLD_S = '#FFD76A';
const BARRIER = '#E14A4A';

export default function NeonDrift() {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const stateRef = useRef(null);
  const pausedRef = useRef(false);
  const advanceTimerRef = useRef(null);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  // Views: map | garage | ready | playing | paused | over
  const [phase, setPhase] = useState('map');
  const [mode, setMode] = useState('campaign'); // campaign | master
  const [fd, setFd] = useState(() => getFD());
  const [muted, setMuted] = useState(() => isSfxMuted());
  useEffect(() => subscribe(() => { setFd(getMeta().fd); setMuted(isSfxMuted()); }), []);

  const { countdown, beginCountdown, cancelCountdown } = useLoungeCountdown();
  // Cancel any in-flight countdown when leaving the playing phase.
  useEffect(() => { if (phase !== 'playing') cancelCountdown(); }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const [distance, setDistance] = useState(0);
  const [tokens, setTokens] = useState(0);
  const [nearMisses, setNearMisses] = useState(0);
  const [crashed, setCrashed] = useState(false);
  const [best, setBest] = useState(() => Number(localStorage.getItem(BEST_KEY) || 0));
  const [totalTokens, setTotalTokens] = useState(() => Number(localStorage.getItem(TOKENS_KEY) || 0));
  const [level, setLevelState] = useState(() => Math.min(getFD().lastLevel || 1, getFD().highestUnlocked || 1));
  const levelRef = useRef(level);
  useEffect(() => { levelRef.current = level; }, [level]);
  const levelCfg = useMemo(() => (mode === 'master' ? masterCfg() : getFDLevelConfig(level)), [level, mode]);
  const world = useMemo(() => worldForLevel(level), [level]);
  const target = levelCfg.tokenMax ?? levelCfg.tokenGoal;
  const [showCongrats, setShowCongrats] = useState(false);
  const [levelDone, setLevelDone] = useState(false);
  const [runStars, setRunStars] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);

  /* ----- Canvas sizing — smaller portrait so HUD isn't obscured ----- */
  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapperRef.current;
    if (!canvas || !wrap) return null;
    // Cap width tighter and use a 5:6 aspect (portrait but not overly tall).
    const w = Math.min(520, wrap.clientWidth);
    let h = Math.round(w * (6 / 5));
    // Full-screen embed (native WebView): grow the canvas to fill the
    // available viewport height instead of a fixed portrait aspect.
    if (canvas.closest('[data-embed]')) {
      const top = wrap.getBoundingClientRect().top;
      const avail = Math.round((window.visualViewport?.height || window.innerHeight) - top - 12);
      if (avail > h) h = Math.min(avail, 1000);
    }
    canvas.width = w;
    canvas.height = h;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    return { w, h };
  }, []);

  /* ----- Build fresh game state ----- */
  const buildState = useCallback((W, H) => {
    const roadMargin = W * 0.06;
    const roadW = W - roadMargin * 2;
    const laneW = roadW / LANE_COUNT;
    const cfg = diffConfig(levelRef.current);
    const durationCfg = (mode === 'master') ? 999 : (cfg.duration ?? 30);
    const weather = (mode === 'master') ? 'aurora-deep' : (getFDLevelConfig(levelRef.current)?.weather || 'clear');
    return {
      W, H,
      roadMargin, roadW, laneW,
      carY: H * 0.78,
      carW: laneW * 0.58,
      carH: laneW * 1.05,
      lane: 1,
      laneOffset: 1,
      lv: levelRef.current,
      speed: cfg.baseSpeed,
      scrollY: 0,
      obstacles: [],
      tokens: [],
      obsT: 0, tokT: 0,
      obsEvery: cfg.obsRate,
      tokEvery: cfg.tokRate,
      distance: 0,
      collected: 0,
      timeLeft: durationCfg,
      gameOver: false,
      won: false,
      shake: 0,
      curvePhase: 0,
      curveOffset: 0,
      particles: [],
      near: 0,
      weather,
      stars: Array.from({ length: 40 }, () => ({ x: rand(0, W), y: rand(0, H * 0.34), s: rand(0.6, 1.8) })),
      // Weather-specific ambient particles + scenery layer
      ambient: buildAmbient(weather, W, H),
      scenery: buildScenery(weather, W, H),
      lightning: 0,   // 0..1 flash intensity for storm weather
      lightningT: rand(3, 8),
      auroraPhase: rand(0, Math.PI * 2),
    };
  }, [mode]);

  /* ----- Spawners ----- */
  const spawnObstacle = (s) => {
    const lanes = [0, 1, 2];
    const cfg = diffConfig(s.lv);
    // Avoid dropping obstacles onto lanes where a nearby token is falling.
    const tokLanes = new Set();
    const nearY = s.H * 0.4;
    for (const t of s.tokens) if (!t.taken && t.y < nearY) tokLanes.add(t.lane);
    const safe = lanes.filter((l) => !tokLanes.has(l));
    if (safe.length === 0) return;
    const doubles = safe.length >= 2 && Math.random() < cfg.doubleObsProb;
    const blocked = doubles ? shuffle(safe).slice(0, 2) : [safe[Math.floor(Math.random() * safe.length)]];
    for (const ln of blocked) {
      s.obstacles.push({
        lane: ln, y: -60, h: 44,
        kind: Math.random() < 0.65 ? 'car' : (Math.random() < 0.5 ? 'barrier' : 'debris'),
        passed: false,
      });
    }
  };
  const spawnToken = (s) => {
    // Prefer lanes that have no obstacle in the "landing zone" (top half of canvas)
    // so tokens are never stuck right next to a barrier.
    const busy = new Set();
    const dangerY = s.H * 0.55;
    for (const o of s.obstacles) {
      if (o.y < dangerY) busy.add(o.lane);
    }
    const candidates = [0, 1, 2].filter((l) => !busy.has(l));
    const pool = candidates.length ? candidates : [0, 1, 2];
    const ln = pool[Math.floor(Math.random() * pool.length)];
    s.tokens.push({ lane: ln, y: -30, r: 16, taken: false, pulse: rand(0, Math.PI * 2) });
  };

  /* ----- Collisions ----- */
  const carLaneRect = (s) => {
    const laneX = s.roadMargin + (s.laneOffset + 0.5) * s.laneW;
    return {
      x: laneX - s.carW / 2,
      y: s.carY - s.carH / 2,
      w: s.carW,
      h: s.carH,
    };
  };
  const obsRect = (s, o) => {
    const laneX = s.roadMargin + (o.lane + 0.5) * s.laneW;
    const w = s.laneW * 0.68;
    return { x: laneX - w / 2, y: o.y, w, h: o.h };
  };
  const rectHit = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

  const winLevel = () => {
    const s = stateRef.current;
    if (!s) return;
    s.gameOver = true;
    const runTokens = s.collected;
    const runDistance = Math.floor(s.distance);
    const runNearMisses = s.near || 0;

    // Persist raw best (metres) for legacy display
    const newBest = Math.max(best, runDistance);
    setBest(newBest);
    localStorage.setItem(BEST_KEY, String(newBest));

    if (mode === 'master') {
      // Master Drive — no level clear, just a distance/token high score
      saveFDMaster(runDistance);
      recordRun('drive', {
        level: 0, distance: runDistance, tokens: runTokens,
        perfect: false, nearMisses: runNearMisses, cleared: false, mode: 'master',
      });
      playSfx('levelClear');
      setLevelDone(false);
      setShowCongrats(false);
      setCrashed(false);
      setPhase('over');
      return;
    }

    // Campaign win: award stars, unlock garage cosmetics at milestones
    const stars = computeFDStars(levelCfg, { survived: true, tokens: runTokens, nearMisses: runNearMisses });
    const nextHighest = Math.min(FD_MAX_LEVEL, levelRef.current + 1);
    const unlocks = [];
    // Garage unlocks at L5/L10/L20 tied to the level being cleared
    for (const skin of GARAGE_SKINS) if (skin.unlockLevel === levelRef.current) unlocks.push(`skin:${skin.id}`);
    for (const trail of GARAGE_TRAILS) if (trail.unlockLevel === levelRef.current) unlocks.push(`trail:${trail.id}`);

    saveFDLevelResult(levelRef.current, { stars, tokens: runTokens, distance: runDistance,
                                          nearMisses: runNearMisses, cleared: true }, unlocks);

    recordRun('drive', {
      level: levelRef.current, distance: runDistance, tokens: runTokens,
      perfect: stars === 3, nearMisses: runNearMisses, cleared: true, stars,
    });
    playSfx('levelClear');
    if (stars === 3) playSfx('star', { volume: 0.35 });
    if (unlocks.length > 0) setTimeout(() => playSfx('unlock', { volume: 0.4 }), 400);

    setRunStars(stars);
    setLevelDone(true);
    setShowCongrats(true);
    setCrashed(false);
    setPhase('over');
    if (levelRef.current < FD_MAX_LEVEL) {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(() => {
        advanceTimerRef.current = null;
        setShowCongrats(false);
        setLevelState((l) => Math.min(l + 1, FD_MAX_LEVEL));
        setTimeout(startPlaying, 60);
      }, 2400);
    }
  };

  const die = () => {
    const s = stateRef.current;
    if (!s || s.gameOver) return;
    s.gameOver = true;
    s.shake = 24;
    // debris burst
    for (let i = 0; i < 24; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = rand(80, 240);
      s.particles.push({
        x: carLaneRect(s).x + s.carW / 2, y: s.carY, vx: Math.cos(a) * v, vy: Math.sin(a) * v,
        life: 0.9, maxLife: 0.9, size: rand(2, 4), color: Math.random() < 0.5 ? GOLD : BARRIER,
      });
    }
    // Persist best + tokens
    const runDistance = Math.floor(s.distance);
    const newBest = Math.max(best, runDistance);
    setBest(newBest);
    localStorage.setItem(BEST_KEY, String(newBest));
    setTotalTokens((tt) => {
      const nt = tt + s.collected;
      localStorage.setItem(TOKENS_KEY, String(nt));
      return nt;
    });
    if (mode === 'campaign') {
      // Crash mid-level = failure (0 stars). Progression via survival only.
      setFDLastLevel(levelRef.current);
      setRunStars(0);
      recordRun('drive', {
        level: levelRef.current, distance: runDistance, tokens: s.collected,
        perfect: false, nearMisses: s.near || 0, cleared: false, stars: 0,
      });
    } else {
      // Master mode crash: still records + updates masterBest
      saveFDMaster(runDistance);
      recordRun('drive', {
        level: 0, distance: runDistance, tokens: s.collected,
        perfect: false, nearMisses: s.near || 0, cleared: false, mode: 'master',
      });
    }
    playSfx('collision');
    setCrashed(true);
    setPhase('over');
  };

  /* ----- Main loop ----- */
  const loop = useCallback((ts) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const s = stateRef.current;
    if (!s) return;
    const ctx = canvas.getContext('2d');
    const dt = lastTsRef.current ? Math.min(0.05, (ts - lastTsRef.current) / 1000) : 0;
    lastTsRef.current = ts;

    if (!pausedRef.current && !s.gameOver) {
      // Survival-based win: countdown timer → clear
      if (mode === 'campaign' && !s.won) {
        s.timeLeft = Math.max(0, s.timeLeft - dt);
        if (Math.floor(s.timeLeft * 4) !== Math.floor((s.timeLeft + dt) * 4)) {
          setTimeLeft(s.timeLeft);
        }
        if (s.timeLeft <= 0) {
          s.won = true;
          winLevel();
        }
      }
      // Ease lane offset toward target lane (soft spring)
      s.laneOffset += (s.lane - s.laneOffset) * Math.min(1, dt * 12);

      // Curve sway
      s.curvePhase += dt * diffConfig(s.lv).curveFreq * Math.PI;
      s.curveOffset = Math.sin(s.curvePhase) * diffConfig(s.lv).curveAmp;

      // Speed ramp
      const cfg = diffConfig(s.lv);
      s.speed = Math.min(cfg.maxSpeed, s.speed + dt * (cfg.baseSpeed * cfg.ramp * 6));
      const scroll = s.speed * dt;
      s.scrollY += scroll;
      s.distance = s.scrollY / 10;
      setDistance(Math.floor(s.distance));

      // Spawn obstacles
      s.obsT += dt;
      if (s.obsT >= s.obsEvery) {
        s.obsT = 0;
        spawnObstacle(s);
      }
      // Spawn tokens
      s.tokT += dt;
      if (s.tokT >= s.tokEvery) {
        s.tokT = 0;
        spawnToken(s);
      }

      // Move obstacles + tokens
      for (const o of s.obstacles) o.y += scroll;
      for (const t of s.tokens) { t.y += scroll; t.pulse += dt * 4; }
      s.obstacles = s.obstacles.filter((o) => o.y < s.H + 80);
      s.tokens = s.tokens.filter((t) => t.y < s.H + 40 && !t.taken);

      // Collision + collect + near-miss
      const car = carLaneRect(s);
      for (const o of s.obstacles) {
        const r = obsRect(s, o);
        if (rectHit(car, r)) { die(); break; }
        if (!o.passed && o.y > s.carY) {
          o.passed = true;
          // Near-miss: obstacle in an ADJACENT lane, passed while overlapping our y-range vertically
          if (Math.abs(o.lane - s.lane) === 1) {
            const overlapY = Math.abs((r.y + r.h / 2) - (car.y + car.h / 2)) < (car.h * 0.6);
            if (overlapY) { s.near = (s.near || 0) + 1; }
          }
        }
      }
      if (s.gameOver) { /* continue to render */ }
      for (const t of s.tokens) {
        if (t.taken) continue;
        const laneX = s.roadMargin + (t.lane + 0.5) * s.laneW;
        const dx = (car.x + car.w / 2) - laneX;
        const dy = (car.y + car.h / 2) - t.y;
        if (Math.abs(dx) < s.laneW * 0.55 && Math.abs(dy) < 34) {
          t.taken = true;
          s.collected += 1;
          setTokens(s.collected);
          playSfx('orb', { volume: 0.3 });
          // Sparkle burst
          for (let i = 0; i < 8; i++) {
            const a = Math.random() * Math.PI * 2;
            const v = rand(30, 90);
            s.particles.push({
              x: laneX, y: t.y, vx: Math.cos(a) * v, vy: Math.sin(a) * v,
              life: 0.6, maxLife: 0.6, size: rand(2, 3), color: GOLD_S,
            });
          }
        }
      }
      // Particles
      for (const p of s.particles) {
        p.life -= dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 400 * dt * 0.4;
      }
      s.particles = s.particles.filter((p) => p.life > 0);

      // Shake decay
      s.shake = Math.max(0, s.shake - dt * 30);
    }

    /* ---- Render ---- */
    const { W, H } = s;
    const cityY = H * 0.34;

    // Weather-driven scene: sky + sun/moon + silhouettes + ambient particles
    drawWeatherScene(ctx, s, W, H, cityY, dt);

    // Screen shake offset
    const shx = s.shake ? rand(-s.shake, s.shake) : 0;
    const shy = s.shake ? rand(-s.shake, s.shake) : 0;
    ctx.save();
    ctx.translate(shx, shy);

    // Road (with subtle horizontal sway) — brighter for visibility
    const roadX = s.roadMargin + s.curveOffset * 0.2;
    const roadGrad = ctx.createLinearGradient(0, cityY, 0, H);
    roadGrad.addColorStop(0, '#152036');
    roadGrad.addColorStop(1, '#0A1122');
    ctx.fillStyle = roadGrad;
    ctx.fillRect(roadX, cityY, s.roadW, H - cityY);
    // Road edges — brighter neon
    ctx.fillStyle = GOLD_S;
    ctx.fillRect(roadX - 3, cityY, 3, H - cityY);
    ctx.fillRect(roadX + s.roadW, cityY, 3, H - cityY);
    // Lane dashes — high-contrast bright
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    const dashOffset = (s.scrollY % 32);
    for (let l = 1; l < LANE_COUNT; l++) {
      const x = roadX + l * s.laneW - 1.5;
      for (let y = -32 + dashOffset; y < H; y += 32) {
        ctx.fillRect(x, y + cityY, 3, 18);
      }
    }

    // Tokens
    for (const t of s.tokens) {
      if (t.taken) continue;
      const laneX = roadX + (t.lane + 0.5) * s.laneW;
      const r = t.r + Math.sin(t.pulse) * 1.5;
      ctx.save();
      ctx.translate(laneX, t.y);
      ctx.rotate(t.pulse * 0.6);
      // outer glow
      ctx.shadowColor = GOLD_S;
      ctx.shadowBlur = 16;
      ctx.fillStyle = GOLD;
      ctx.fillRect(-r, -r, r * 2, r * 2);
      ctx.shadowBlur = 0;
      ctx.fillStyle = GOLD_S;
      ctx.fillRect(-r + 2, -r + 2, r * 2 - 4, r * 2 - 4);
      ctx.fillStyle = NAVY;
      ctx.font = `700 ${Math.round(r)}px system-ui`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('B+', 0, 0);
      ctx.restore();
    }

    // Obstacles
    for (const o of s.obstacles) {
      const r = obsRect(s, o);
      if (o.kind === 'car') {
        ctx.fillStyle = '#1B2947';
        ctx.fillRect(r.x, r.y, r.w, r.h);
        ctx.fillStyle = '#4E6690';
        ctx.fillRect(r.x + 3, r.y + 4, r.w - 6, 6); // rear window
        ctx.fillStyle = '#FF5A4A';
        ctx.fillRect(r.x + 4, r.y + r.h - 6, 4, 3); // brake lights
        ctx.fillRect(r.x + r.w - 8, r.y + r.h - 6, 4, 3);
      } else if (o.kind === 'barrier') {
        // hazard-stripe barrier
        ctx.fillStyle = BARRIER;
        ctx.fillRect(r.x, r.y, r.w, r.h);
        ctx.fillStyle = '#F3E9C8';
        for (let dxs = -r.h; dxs < r.w; dxs += 10) {
          ctx.fillRect(r.x + dxs, r.y, 5, r.h);
        }
      } else {
        ctx.fillStyle = '#151A24';
        ctx.fillRect(r.x + 4, r.y + 8, r.w - 8, r.h - 16);
        ctx.fillStyle = '#31241A';
        ctx.fillRect(r.x + 8, r.y + 12, r.w - 16, 4);
      }
    }

    // Car — top-down realistic rendering
    const car = carLaneRect(s);
    drawRealisticCar(ctx, car, skinById(fd.garage?.currentSkin || 'gold'), trailById(fd.garage?.currentTrail || 'none'));

    // Particles
    for (const p of s.particles) {
      const a = clamp(p.life / p.maxLife, 0, 1);
      ctx.globalAlpha = a;
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x, p.y, p.size, p.size);
    }
    ctx.globalAlpha = 1;

    ctx.restore();

    rafRef.current = requestAnimationFrame(loop);
    // Game loop reads all mutable state via refs (stateRef, pausedRef, lastTsRef).
    // Adding update/render/die/mode/winLevel here would re-create the callback
    // every render and restart RAF mid-run. Intentional stable-closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ----- Controls ----- */
  const move = (dir) => {
    const s = stateRef.current;
    if (!s || s.gameOver) return;
    s.lane = clamp(s.lane + dir, 0, LANE_COUNT - 1);
  };

  const startPlaying = () => {
    const dim = fit();
    if (!dim) return;
    stateRef.current = buildState(dim.w, dim.h);
    setDistance(0); setTokens(0); setNearMisses(0); setCrashed(false); setRunStars(0);
    setTimeLeft(mode === 'campaign' ? (levelCfg.duration ?? 0) : 0);
    setPhase('playing');
    setLevelDone(false);
    setShowCongrats(false);
    // Freeze physics during 3-2-1-GO countdown; render still runs.
    pausedRef.current = true;
    lastTsRef.current = 0;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(loop);
    playSfx('button', { volume: 0.3 });
    beginCountdown(() => {
      pausedRef.current = false;
      lastTsRef.current = 0;
    });
  };
  // (Auto-advance handled inside winLevel — no manual advance needed.)
  const togglePause = () => {
    if (phase !== 'playing' && phase !== 'paused') return;
    if (countdown) return; // Ignore during countdown
    if (phase === 'playing') { pausedRef.current = true; setPhase('paused'); }
    else { pausedRef.current = false; setPhase('playing'); lastTsRef.current = 0; }
  };

  /* ----- Mount ----- */
  useEffect(() => {
    fit();
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const g = ctx.createLinearGradient(0, 0, 0, canvas.height);
      g.addColorStop(0, NAVY); g.addColorStop(1, NAVY_2);
      ctx.fillStyle = g; ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    const onResize = () => fit();
    window.addEventListener('resize', onResize);
    const onVis = () => { if (document.hidden && phase === 'playing') { pausedRef.current = true; setPhase('paused'); } };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
    };
    // Mount-only setup — `phase` intentionally read via closure inside onVis
    // (worst case the tab-hide handler no-ops when phase drifts, which is fine).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fit]);

  /* ----- Keyboard ----- */
  useEffect(() => {
    if (phase !== 'playing' && phase !== 'paused') return;
    const onDown = (e) => {
      if (['ArrowLeft', 'KeyA'].includes(e.code)) { e.preventDefault(); move(-1); }
      else if (['ArrowRight', 'KeyD'].includes(e.code)) { e.preventDefault(); move(1); }
      else if (e.code === 'Space') { e.preventDefault(); togglePause(); }
    };
    window.addEventListener('keydown', onDown);
    return () => window.removeEventListener('keydown', onDown);
    // togglePause is defined above with stable-through-refs behaviour; adding
    // it would cause the keydown listener to re-bind every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  /* ----- Touch / pointer swipe ----- */
  const touchRef = useRef({ x: 0, y: 0, t: 0 });
  const onPointerDown = (e) => {
    // Ignore taps on interactive overlay controls (Play again / Next level / Level select / Pause)
    if (e.target && e.target.closest && e.target.closest('button')) return;
    if (phase === 'ready') { startPlaying(); return; }
    if (phase !== 'playing') return;
    touchRef.current = { x: e.clientX, y: e.clientY, t: performance.now() };
  };
  const onPointerUp = (e) => {
    if (phase !== 'playing') return;
    const dx = e.clientX - touchRef.current.x;
    const dy = e.clientY - touchRef.current.y;
    const dt = performance.now() - touchRef.current.t;
    if (dt < 400 && Math.abs(dx) > 24 && Math.abs(dx) > Math.abs(dy)) {
      move(dx > 0 ? 1 : -1);
    } else if (dt < 300 && Math.abs(dx) < 12 && Math.abs(dy) < 12) {
      // Simple tap → left/right of center
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (rect) {
        const midX = rect.left + rect.width / 2;
        move(e.clientX < midX ? -1 : 1);
      }
    }
  };

  const openMap = () => { if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; } setPhase('map'); setMode('campaign'); };
  const scrollSectionIntoView = () => {
    // Give React one frame to render the shorter Ready view before scrolling,
    // otherwise the browser scroll-anchors to the old (taller) map layout.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    });
  };
  const pickCampaignLevel = (id) => {
    setLevelState(id); setMode('campaign'); setPhase('ready');
    setFDLastLevel(id); playSfx('button', { volume: 0.3 }); scrollSectionIntoView();
  };
  const startMaster = () => { setMode('master'); setPhase('ready'); playSfx('unlock', { volume: 0.35 }); scrollSectionIntoView(); };

  // Map view — hero + level select map + garage
  if (phase === 'map') {
    return (
      <div ref={sectionRef} className="bg-white rounded-3xl card-soft border border-navy/5 overflow-hidden scroll-mt-4" data-testid="focus-drive">
        <div className="relative w-full aspect-[16/9] overflow-hidden"
             style={{ background: 'linear-gradient(135deg, #0B192C 0%, #12253F 100%)' }} data-testid="focus-drive-banner">
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-4 text-center">
            <p className="text-[10px] uppercase tracking-[0.32em] text-gold font-semibold">Focus Drive</p>
            <p className="font-heading text-2xl lg:text-3xl text-white/90 mt-2">Keep moving forward.</p>
          </div>
          <img src="/images/lounge/focus-drive.png" alt="Focus Drive — Keep moving forward."
               className="absolute inset-0 w-full h-full object-cover" loading="lazy"
               onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
        </div>
        <div className="p-4 lg:p-5 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-navy/40 font-semibold">Campaign progress</p>
              <p className="font-heading text-lg text-navy mt-0.5">
                {fd.highestUnlocked - 1} of {FD_MAX_LEVEL} levels · <span className="text-gold">
                  {Object.values(fd.levelStars).reduce((a, b) => a + b, 0)}
                </span> / {FD_MAX_LEVEL * 3} stars
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setSfxMuted(!muted)} data-testid="fd-mute"
                      className="rounded-full w-9 h-9 flex items-center justify-center border border-navy/10 text-navy/70 hover:text-navy hover:border-navy/25 transition-smooth"
                      aria-label={muted ? 'Unmute' : 'Mute'}>
                {muted ? <VolumeX size={14}/> : <Volume2 size={14}/>}
              </button>
              <button type="button" onClick={() => pickCampaignLevel(Math.min(fd.lastLevel || 1, fd.highestUnlocked || 1))}
                      data-testid="fd-resume"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-white font-semibold text-sm hover:brightness-110 transition-smooth">
                Resume Level {Math.min(fd.lastLevel || 1, fd.highestUnlocked || 1)} <ChevronRight size={14}/>
              </button>
            </div>
          </div>

          <FocusDriveLevelSelect
            fd={fd}
            onPick={pickCampaignLevel}
            onGarage={() => { setPhase('garage'); playSfx('button', { volume: 0.3 }); }}
            onMaster={startMaster}
          />
          <p className="text-center text-xs text-navy/40">
            Clear a level to unlock the next. Skins unlock as you clear levels 5, 10, and 20.
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'garage') {
    return (
      <div ref={sectionRef} className="bg-white rounded-3xl card-soft border border-navy/5 overflow-hidden p-4 lg:p-6 scroll-mt-4" data-testid="focus-drive">
        <FocusDriveGarage fd={fd} onBack={() => setPhase('map')} onChange={() => setFd(getFD())}/>
      </div>
    );
  }

  const skin = skinById(fd.garage?.currentSkin || 'gold');
  const trail = trailById(fd.garage?.currentTrail || 'none');
  const bestStars = fd.levelStars[level] ?? 0;

  return (
    <div ref={sectionRef} className="bg-white rounded-3xl card-soft border border-navy/5 overflow-hidden scroll-mt-4" data-testid="focus-drive">
      <div className="px-4 lg:px-5 pt-4 pb-2 flex items-center justify-between gap-3">
        <button type="button" onClick={openMap} data-testid="fd-back-map"
                className="text-xs text-navy/60 hover:text-navy inline-flex items-center gap-1.5 transition-smooth">
          ← Level select
        </button>
        <div className="text-center">
          <p className="text-[9px] uppercase tracking-[0.28em] text-navy/40 font-semibold">
            {mode === 'master' ? 'Master Drive' : `World ${world.id} · ${world.name}`}
          </p>
          <p className="font-heading text-base text-navy mt-0.5">
            {mode === 'master' ? 'The hardest run of your life' : `Level ${level} — survive ${levelCfg.duration}s`}
          </p>
        </div>
        <button type="button" onClick={openMap} data-testid="focus-drive-home"
                className="text-xs text-navy/60 hover:text-navy inline-flex items-center gap-1.5 transition-smooth"
                aria-label="Back to level select">
          <Home size={13}/>
        </button>
      </div>

      <div className="p-4 lg:p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className="text-[10px] uppercase tracking-widest text-navy/40">Swipe / A / D · Space to pause</p>
          <p className="text-[10px] uppercase tracking-widest text-navy/40">
            Skin · <span className="text-navy/70">{skin.name}</span>
          </p>
        </div>

        <div
          ref={wrapperRef}
          className="relative mx-auto rounded-2xl overflow-hidden border border-navy/10 select-none touch-none w-full"
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <canvas ref={canvasRef} className={`block w-full ${phase === 'ready' ? 'hidden' : ''}`} data-testid="focus-drive-canvas"/>

          {/* 3-2-1-GO countdown (shared across all Lounge games) */}
          <LoungeCountdown n={countdown} testid="fd-countdown" color="#F5DAA6" />

          {/* HUD inside canvas */}
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2 pointer-events-none z-10">
            <div className="flex flex-col gap-2">
              <div className="rounded-xl px-3 py-1.5 bg-black/50 backdrop-blur-md border border-white/10">
                <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Distance</p>
                <p className="font-heading text-xl text-white leading-none mt-0.5 tabular-nums" data-testid="focus-drive-distance">{distance}m</p>
              </div>
              {mode === 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/50 backdrop-blur-md border border-white/10">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Level</p>
                  <p className="font-heading text-xl text-white leading-none mt-0.5 tabular-nums" data-testid="focus-drive-level">{level}<span className="text-white/40 text-sm">/{FD_MAX_LEVEL}</span></p>
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              {mode === 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/50 backdrop-blur-md border border-gold/30 text-right">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Time Left</p>
                  <p className="font-heading text-xl text-gold leading-none mt-0.5 tabular-nums" data-testid="focus-drive-timer">
                    {formatFDTime(timeLeft)}
                  </p>
                </div>
              )}
              <div className="rounded-xl px-3 py-1.5 bg-black/50 backdrop-blur-md border border-white/10 text-right">
                <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Tokens</p>
                <p className="font-heading text-lg text-white leading-none mt-0.5 tabular-nums" data-testid="focus-drive-goal">
                  <span className="text-gold">{tokens}</span>{mode === 'campaign' ? ` / ${target}` : ''}
                </p>
              </div>
              {mode === 'campaign' && (
                <div className="rounded-xl px-3 py-1.5 bg-black/50 backdrop-blur-md border border-white/10 text-right">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Best</p>
                  <div className="mt-0.5"><StarBadge count={bestStars} size={10} testid="fd-hud-best-stars"/></div>
                </div>
              )}
              {mode === 'master' && (fd.masterBest ?? 0) > 0 && (
                <div className="rounded-xl px-3 py-1.5 bg-black/50 backdrop-blur-md border border-white/10 text-right">
                  <p className="text-[9px] uppercase tracking-widest text-white/60 leading-none">Master best</p>
                  <p className="font-heading text-base text-gold leading-none mt-0.5 tabular-nums" data-testid="fd-master-best">{fd.masterBest}m</p>
                </div>
              )}
            </div>
          </div>

          {(phase === 'playing' || phase === 'paused') && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-auto z-10">
              <button onClick={togglePause} data-testid="focus-drive-pause"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-navy bg-gold hover:brightness-95 transition-smooth shadow">
                {phase === 'paused' ? <><Play size={12}/> Resume</> : <><Pause size={12}/> Pause</>}
              </button>
            </div>
          )}

          {phase === 'ready' && (
            <div className="relative w-full backdrop-blur-md flex flex-col items-center text-white z-20 px-5 py-8 text-center"
                 style={{ background: 'linear-gradient(135deg, rgba(11,25,44,0.9) 0%, rgba(18,37,63,0.94) 100%)' }}
                 data-testid="focus-drive-ready">
              <div className="my-auto w-full flex flex-col items-center py-6">
              <p className="text-[10px] uppercase tracking-[0.32em] text-gold font-semibold">
                {mode === 'master' ? 'Master Drive' : `Level ${level} · ${world.name}`}
              </p>
              <p className="font-heading text-3xl lg:text-4xl mt-2">
                {mode === 'master' ? 'The hardest run' : `Survive ${levelCfg.duration}s`}
              </p>
              {mode === 'campaign' && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-xs text-white/75">
                  <span className="px-2.5 py-1 rounded-full bg-white/8 border border-white/10">Survive · <span className="text-gold tabular-nums">{levelCfg.duration}s</span></span>
                  <span className="px-2.5 py-1 rounded-full bg-white/8 border border-white/10 inline-flex items-center gap-1">
                    2<Sparkles size={10} className="text-gold"/> collect <span className="tabular-nums">{levelCfg.starThresholds.two}</span>+ tokens
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-white/8 border border-white/10 inline-flex items-center gap-1">
                    3<Sparkles size={10} className="text-gold"/> <span className="tabular-nums">{levelCfg.starThresholds.three}</span>+ tokens, 0 close-calls
                  </span>
                </div>
              )}
              <div className="mt-4 max-w-sm space-y-1.5 text-sm text-white/85 leading-relaxed">
                <p><span className="text-gold font-semibold">Swipe</span> or press <span className="text-gold font-semibold">← →</span> to change lanes</p>
                <p><span className="text-gold font-semibold">Grab</span> tokens · <span className="text-gold font-semibold">Dodge</span> traffic &amp; barriers</p>
              </div>
              <button onClick={startPlaying} data-testid="focus-drive-start"
                className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-navy font-semibold hover:brightness-95 transition-smooth">
                <Play size={15}/> Start
              </button>
              </div>
            </div>
          )}

          {phase === 'over' && !showCongrats && (
            <div className="absolute inset-0 bg-navy/90 backdrop-blur-md flex flex-col items-center justify-center text-white z-20 px-6 text-center"
                 data-testid="focus-drive-over">
              <p className={`text-[10px] uppercase tracking-[0.28em] font-semibold ${crashed ? 'text-red-300' : 'text-gold'}`}>
                {crashed ? 'You crashed' : (mode === 'master' ? 'End of run' : `Level ${level} clear · you survived`)}
              </p>
              {crashed && mode === 'campaign' && (
                <p className="text-xs text-white/60 mt-2 max-w-xs">Survive the timer to earn <span className="text-gold font-semibold">⭐</span>. Tokens are optional — they raise your star count.</p>
              )}
              {mode === 'campaign' && !crashed && runStars > 0 && (
                <div className="mt-3"><StarBadge count={runStars} size={26} testid="fd-over-stars"/></div>
              )}
              <p className="font-heading text-3xl mt-3">
                {tokens} <span className="text-white/50 text-base">tokens</span>
              </p>
              <p className="text-sm text-white/80 mt-1 tabular-nums">{distance}m</p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <button onClick={startPlaying} data-testid="focus-drive-restart"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gold text-navy font-semibold hover:brightness-95 text-sm">
                  <RotateCcw size={14}/> Play again
                </button>
                {mode === 'campaign' && !crashed && level < FD_MAX_LEVEL && (
                  <button onClick={() => {
                    if (advanceTimerRef.current) { clearTimeout(advanceTimerRef.current); advanceTimerRef.current = null; }
                    setLevelState(l => Math.min(l + 1, FD_MAX_LEVEL));
                    setTimeout(startPlaying, 60);
                  }}
                          data-testid="focus-drive-next"
                          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 text-white font-semibold hover:bg-white/15 text-sm">
                    Next level <ChevronRight size={14}/>
                  </button>
                )}
                <button onClick={openMap} data-testid="focus-drive-over-map"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/80 hover:text-white text-sm">
                  Level select
                </button>
              </div>
            </div>
          )}
          <Congrats
            open={showCongrats}
            title={`Level ${level} clear!`}
            subtitle={level < FD_MAX_LEVEL
              ? `${tokens} tokens · Level ${level + 1} incoming…`
              : `You just cleared all ${FD_MAX_LEVEL} levels. Master Drive unlocked.`}
            canAdvance={false}
            onPlayAgain={startPlaying}
            testid="focus-drive-congrats"
          />
        </div>

        <p className="text-[11px] text-navy/40 mt-3 text-center hidden lg:block">
          Desktop: ← → or A / D · Mobile: swipe left / right · Space to pause.
        </p>
      </div>
    </div>
  );
}

function masterCfg() {
  // Master Drive — fastest scroll, tight obstacle cadence, aurora backdrop feel.
  return {
    tokenMax: 999,
    duration: 999,
    baseSpeed: 380, maxSpeed: 560,
    obsRate: 0.65, tokRate: 0.9,
    ramp: 0.06, curveAmp: 60, curveFreq: 0.36,
    doubleObsProb: 0.42,
    starThresholds: { one: Infinity, two: Infinity, three: Infinity },
  };
}

function formatFDTime(secs) {
  const s = Math.max(0, Math.ceil(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}:${String(r).padStart(2, '0')}` : `${r}s`;
}

/**
 * drawRealisticCar — top-down premium car rendering.
 * Shows hood, roof, tinted windows, wheels on each side, headlights, taillights,
 * subtle body-color shading, optional glowing trail.
 */
function drawRealisticCar(ctx, car, skin, trail) {
  const { x, y, w, h } = car;
  const bodyColor = skin?.color || '#C5A059';
  const accent    = skin?.accent || '#E1C88A';

  // Trail (behind car — draws first so it sits under the body)
  if (trail && trail.color && trail.color !== 'transparent') {
    ctx.save();
    const g = ctx.createLinearGradient(x + w / 2, y + h, x + w / 2, y + h + 60);
    g.addColorStop(0, trail.color + 'CC');
    g.addColorStop(1, trail.color + '00');
    ctx.fillStyle = g;
    ctx.fillRect(x + w * 0.28, y + h - 2, w * 0.44, 60);
    ctx.shadowColor = trail.color;
    ctx.shadowBlur = 14;
    ctx.fillRect(x + w * 0.42, y + h, w * 0.16, 40);
    ctx.restore();
  }

  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = 10;
  ctx.shadowOffsetY = 3;

  // Wheels (dark rounded rects on each side, front + rear)
  const wheelW = Math.max(3, w * 0.10);
  const wheelH = Math.max(6, h * 0.16);
  ctx.fillStyle = '#0A0F17';
  roundRect(ctx, x - wheelW * 0.25, y + h * 0.16, wheelW, wheelH, 2); ctx.fill();
  roundRect(ctx, x + w - wheelW * 0.75, y + h * 0.16, wheelW, wheelH, 2); ctx.fill();
  roundRect(ctx, x - wheelW * 0.25, y + h * 0.66, wheelW, wheelH, 2); ctx.fill();
  roundRect(ctx, x + w - wheelW * 0.75, y + h * 0.66, wheelW, wheelH, 2); ctx.fill();

  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // Body — rounded rect with vertical gradient (highlight to base)
  const bodyGrad = ctx.createLinearGradient(x, y, x, y + h);
  bodyGrad.addColorStop(0, lighten(bodyColor, 22));
  bodyGrad.addColorStop(0.45, bodyColor);
  bodyGrad.addColorStop(1, darken(bodyColor, 28));
  ctx.fillStyle = bodyGrad;
  roundRect(ctx, x, y, w, h, Math.min(w, h) * 0.14); ctx.fill();

  // Subtle top-highlight
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  roundRect(ctx, x + w * 0.08, y + h * 0.05, w * 0.84, h * 0.10, 4); ctx.fill();

  // Windshield (front — angled, top)
  const winGrad = ctx.createLinearGradient(x, y + h * 0.18, x, y + h * 0.42);
  winGrad.addColorStop(0, 'rgba(20,32,56,0.9)');
  winGrad.addColorStop(1, 'rgba(20,32,56,0.65)');
  ctx.fillStyle = winGrad;
  ctx.beginPath();
  ctx.moveTo(x + w * 0.16, y + h * 0.20);
  ctx.lineTo(x + w * 0.84, y + h * 0.20);
  ctx.lineTo(x + w * 0.76, y + h * 0.40);
  ctx.lineTo(x + w * 0.24, y + h * 0.40);
  ctx.closePath();
  ctx.fill();

  // Rear window (smaller trapezoid)
  ctx.fillStyle = 'rgba(20,32,56,0.75)';
  ctx.beginPath();
  ctx.moveTo(x + w * 0.24, y + h * 0.58);
  ctx.lineTo(x + w * 0.76, y + h * 0.58);
  ctx.lineTo(x + w * 0.80, y + h * 0.76);
  ctx.lineTo(x + w * 0.20, y + h * 0.76);
  ctx.closePath();
  ctx.fill();

  // Roof strip between the two windows
  ctx.fillStyle = darken(bodyColor, 12);
  ctx.fillRect(x + w * 0.20, y + h * 0.40, w * 0.60, h * 0.18);

  // Accent trim line down the sides
  ctx.fillStyle = accent;
  ctx.fillRect(x + w * 0.10, y + h * 0.48, 1.5, h * 0.10);
  ctx.fillRect(x + w * 0.90 - 1.5, y + h * 0.48, 1.5, h * 0.10);

  // Headlights (front, glowing)
  const hlGrad = ctx.createRadialGradient(x + w * 0.20, y + 3, 0, x + w * 0.20, y + 3, 8);
  hlGrad.addColorStop(0, '#FFF5D1');
  hlGrad.addColorStop(1, 'rgba(255,245,209,0)');
  ctx.fillStyle = hlGrad;
  ctx.fillRect(x + w * 0.10, y - 3, 12, 12);
  const hlGrad2 = ctx.createRadialGradient(x + w * 0.80, y + 3, 0, x + w * 0.80, y + 3, 8);
  hlGrad2.addColorStop(0, '#FFF5D1');
  hlGrad2.addColorStop(1, 'rgba(255,245,209,0)');
  ctx.fillStyle = hlGrad2;
  ctx.fillRect(x + w * 0.68, y - 3, 12, 12);
  // Solid headlight bulbs
  ctx.fillStyle = '#FFEAB0';
  ctx.fillRect(x + w * 0.14, y + 2, w * 0.14, 2);
  ctx.fillRect(x + w * 0.72, y + 2, w * 0.14, 2);

  // Taillights (rear, red-ish)
  ctx.fillStyle = '#E14A4A';
  ctx.fillRect(x + w * 0.14, y + h - 4, w * 0.14, 2);
  ctx.fillRect(x + w * 0.72, y + h - 4, w * 0.14, 2);

  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

function lighten(hex, amt) { return shiftHex(hex, amt); }
function darken(hex, amt) { return shiftHex(hex, -amt); }
function shiftHex(hex, amt) {
  const h = (hex || '#C5A059').replace('#', '');
  const num = parseInt(h, 16);
  let r = ((num >> 16) & 0xff) + amt;
  let g = ((num >> 8) & 0xff) + amt;
  let b = (num & 0xff) + amt;
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

/* ---------- Weather-driven scene rendering (per-world atmosphere) ---------- */

/** Palette per weather tag — sky top, sky bottom, silhouette color, accent glow */
const WEATHER_PALETTES = {
  clear:        { top: '#0B192C', bot: '#152848', sil: '#08111F', glow: 'rgba(212,175,55,0.16)' }, // World 1a
  dusk:         { top: '#1E1830', bot: '#4A2C3A', sil: '#0F0A1A', glow: 'rgba(255,140,90,0.30)' }, // World 1b
  neon:         { top: '#0A0F26', bot: '#2A1050', sil: '#04061A', glow: 'rgba(255,60,180,0.32)' }, // World 2a
  rain:         { top: '#0F1826', bot: '#1E2E45', sil: '#080F1B', glow: 'rgba(140,190,230,0.22)' }, // World 2b
  sunset:       { top: '#2E1A40', bot: '#FF7A55', sil: '#160A24', glow: 'rgba(255,180,110,0.40)' }, // World 3a
  coast:        { top: '#25325E', bot: '#F0A570', sil: '#0F1A32', glow: 'rgba(255,200,120,0.32)' }, // World 3b
  storm:        { top: '#0A0C14', bot: '#22283A', sil: '#04060A', glow: 'rgba(180,200,220,0.14)' }, // World 4a
  'rain-heavy': { top: '#0A0E1A', bot: '#1A2130', sil: '#04070E', glow: 'rgba(120,160,200,0.18)' }, // World 4b
  mountain:     { top: '#152030', bot: '#2E3F5A', sil: '#0A121E', glow: 'rgba(160,190,220,0.20)' }, // World 5a
  snow:         { top: '#2A354A', bot: '#5A6A82', sil: '#101820', glow: 'rgba(230,240,250,0.30)' }, // World 5b
  desert:       { top: '#2C1810', bot: '#C87038', sil: '#180C08', glow: 'rgba(255,180,90,0.36)' },  // World 6a
  dust:         { top: '#3A2A18', bot: '#B08858', sil: '#20140A', glow: 'rgba(220,170,110,0.30)' }, // World 6b
  aurora:       { top: '#061020', bot: '#0E2438', sil: '#02060E', glow: 'rgba(120,235,193,0.32)' },  // World 7a
  'aurora-deep':{ top: '#040816', bot: '#0A1830', sil: '#02050C', glow: 'rgba(140,200,255,0.34)' }, // World 7b
};

/** Build ambient particle system per weather. */
function buildAmbient(weather, W, H) {
  if (weather === 'snow' || weather === 'mountain') {
    return Array.from({ length: weather === 'snow' ? 60 : 25 }, () => ({
      x: rand(0, W), y: rand(0, H), s: rand(1.2, 2.6), vy: rand(30, 60), vx: rand(-8, 8), kind: 'snow',
    }));
  }
  if (weather === 'rain' || weather === 'rain-heavy' || weather === 'storm') {
    const count = weather === 'rain-heavy' || weather === 'storm' ? 70 : 45;
    return Array.from({ length: count }, () => ({
      x: rand(0, W), y: rand(0, H), s: rand(0.8, 1.4), vy: rand(500, 700), vx: 0, kind: 'rain',
    }));
  }
  if (weather === 'dust' || weather === 'desert') {
    return Array.from({ length: 40 }, () => ({
      x: rand(0, W), y: rand(0, H), s: rand(1, 2.4), vy: rand(6, 14), vx: rand(-30, -8), kind: 'dust',
    }));
  }
  if (weather === 'neon') {
    // subtle floating neon particles
    return Array.from({ length: 20 }, () => ({
      x: rand(0, W), y: rand(0, H * 0.7), s: rand(1, 2), vy: rand(-6, 6), vx: rand(-4, 4), kind: 'neon',
    }));
  }
  return [];
}

/** Build silhouettes/scenery per weather. */
function buildScenery(weather, W, H) {
  // Palm silhouettes for coast/sunset, mountain triangles for mountain/snow,
  // dune arcs for desert/dust, generic city blocks otherwise.
  const kind = (weather === 'sunset' || weather === 'coast') ? 'palms'
             : (weather === 'mountain' || weather === 'snow') ? 'mountains'
             : (weather === 'desert' || weather === 'dust') ? 'dunes'
             : (weather === 'aurora' || weather === 'aurora-deep') ? 'peaks'
             : 'city';
  if (kind === 'palms') {
    return { kind, items: Array.from({ length: 6 }, () => ({ x: rand(0, W), h: rand(50, 85), lean: rand(-0.35, 0.35) })) };
  }
  if (kind === 'mountains') {
    return { kind, items: Array.from({ length: 5 }, (_, i) => ({ x: (W / 5) * i + rand(-30, 30), h: rand(70, 130), w: rand(140, 220) })) };
  }
  if (kind === 'dunes') {
    return { kind, items: Array.from({ length: 4 }, (_, i) => ({ x: (W / 4) * i + rand(-30, 30), h: rand(30, 55), w: rand(170, 260) })) };
  }
  if (kind === 'peaks') {
    return { kind, items: Array.from({ length: 6 }, (_, i) => ({ x: (W / 6) * i + rand(-20, 20), h: rand(60, 110), w: rand(90, 160) })) };
  }
  // default city
  return { kind: 'city', items: Array.from({ length: 18 }, () => ({ x: rand(0, W), h: rand(20, 70), w: rand(24, 44) })) };
}

function drawWeatherScene(ctx, s, W, H, cityY, dt) {
  const pal = WEATHER_PALETTES[s.weather] || WEATHER_PALETTES.clear;

  // Sky gradient
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, pal.top);
  g.addColorStop(1, pal.bot);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // Stars — only for night-ish weather
  if (s.weather === 'clear' || s.weather === 'aurora' || s.weather === 'aurora-deep' || s.weather === 'mountain' || s.weather === 'dusk') {
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    for (const st of s.stars) ctx.fillRect(st.x, st.y, st.s, st.s);
  }

  // Aurora ribbons — for world 7
  if (s.weather === 'aurora' || s.weather === 'aurora-deep') {
    s.auroraPhase += dt * 0.4;
    drawAurora(ctx, W, H * 0.5, s.auroraPhase, s.weather === 'aurora-deep');
  }

  // Sun/moon — for sunset/coast/desert
  if (s.weather === 'sunset' || s.weather === 'coast' || s.weather === 'desert') {
    const cx = W * 0.72, cy = cityY - 8;
    const rG = ctx.createRadialGradient(cx, cy, 0, cx, cy, 90);
    if (s.weather === 'desert') { rG.addColorStop(0, '#FFD9A0'); rG.addColorStop(1, 'rgba(255,180,90,0)'); }
    else                        { rG.addColorStop(0, '#FFEACC'); rG.addColorStop(1, 'rgba(255,140,90,0)'); }
    ctx.fillStyle = rG; ctx.beginPath(); ctx.arc(cx, cy, 90, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = s.weather === 'desert' ? '#FFC78A' : '#FFE0B0';
    ctx.beginPath(); ctx.arc(cx, cy, 22, 0, Math.PI * 2); ctx.fill();
  }

  // Storm lightning
  if (s.weather === 'storm') {
    s.lightningT -= dt;
    if (s.lightningT <= 0) { s.lightning = 1; s.lightningT = rand(4, 9); }
    if (s.lightning > 0) {
      s.lightning = Math.max(0, s.lightning - dt * 3);
      ctx.fillStyle = `rgba(230,240,255,${s.lightning * 0.35})`;
      ctx.fillRect(0, 0, W, H);
    }
  }

  // Horizon glow (accent, per-palette)
  const gl = ctx.createLinearGradient(0, cityY - 50, 0, cityY + 50);
  gl.addColorStop(0, 'rgba(0,0,0,0)');
  gl.addColorStop(0.5, pal.glow);
  gl.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gl;
  ctx.fillRect(0, cityY - 50, W, 100);

  // Silhouettes
  drawScenery(ctx, s.scenery, pal.sil, cityY, W);

  // Ambient particles (rain, snow, dust, neon)
  updateAndDrawAmbient(ctx, s.ambient, s.weather, W, H, dt);
}

function drawScenery(ctx, scenery, silColor, cityY, W) {
  if (!scenery) return;
  ctx.fillStyle = silColor;
  if (scenery.kind === 'city') {
    for (const c of scenery.items) ctx.fillRect(c.x, cityY - c.h, c.w, c.h);
    // Warm window lights
    ctx.fillStyle = 'rgba(255,215,140,0.35)';
    for (const c of scenery.items) {
      for (let wy = 8; wy < c.h - 4; wy += 8) {
        for (let wx = 4; wx < c.w - 4; wx += 8) {
          if (((c.x + wx + wy) | 0) % 3 === 0) ctx.fillRect(c.x + wx, cityY - c.h + wy, 2, 2);
        }
      }
    }
  } else if (scenery.kind === 'palms') {
    for (const p of scenery.items) {
      // trunk
      ctx.save();
      ctx.translate(p.x, cityY);
      ctx.rotate(p.lean * 0.3);
      ctx.fillRect(-2, -p.h, 4, p.h);
      // fronds
      ctx.beginPath();
      for (let f = 0; f < 6; f++) {
        const ang = (Math.PI * 2 / 6) * f + p.lean;
        const fx = Math.cos(ang) * 22;
        const fy = -p.h + Math.sin(ang) * 8 - 2;
        ctx.moveTo(0, -p.h);
        ctx.quadraticCurveTo(fx * 0.6, fy - 6, fx, fy);
      }
      ctx.strokeStyle = silColor; ctx.lineWidth = 3; ctx.stroke();
      ctx.restore();
    }
  } else if (scenery.kind === 'mountains' || scenery.kind === 'peaks') {
    for (const m of scenery.items) {
      ctx.beginPath();
      ctx.moveTo(m.x - m.w / 2, cityY);
      ctx.lineTo(m.x, cityY - m.h);
      ctx.lineTo(m.x + m.w / 2, cityY);
      ctx.closePath();
      ctx.fill();
      // snow cap for mountains
      if (scenery.kind === 'mountains') {
        ctx.fillStyle = 'rgba(240,245,252,0.85)';
        ctx.beginPath();
        ctx.moveTo(m.x - m.w / 6, cityY - m.h * 0.72);
        ctx.lineTo(m.x, cityY - m.h);
        ctx.lineTo(m.x + m.w / 6, cityY - m.h * 0.72);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = silColor;
      }
    }
  } else if (scenery.kind === 'dunes') {
    for (const d of scenery.items) {
      ctx.beginPath();
      ctx.moveTo(d.x - d.w / 2, cityY);
      ctx.quadraticCurveTo(d.x, cityY - d.h * 1.6, d.x + d.w / 2, cityY);
      ctx.closePath();
      ctx.fill();
    }
  }
}

function updateAndDrawAmbient(ctx, ambient, weather, W, H, dt) {
  if (!ambient || ambient.length === 0) return;
  for (const p of ambient) {
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    if (p.y > H) { p.y = -6; p.x = rand(0, W); }
    if (p.x < -6) p.x = W + 6;
    if (p.x > W + 6) p.x = -6;
  }
  if (weather === 'snow' || weather === 'mountain') {
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    for (const p of ambient) { ctx.beginPath(); ctx.arc(p.x, p.y, p.s, 0, Math.PI * 2); ctx.fill(); }
  } else if (weather === 'rain' || weather === 'rain-heavy' || weather === 'storm') {
    ctx.strokeStyle = weather === 'storm' ? 'rgba(170,200,230,0.55)' : 'rgba(150,190,230,0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const p of ambient) { ctx.moveTo(p.x, p.y); ctx.lineTo(p.x - 2, p.y + 10); }
    ctx.stroke();
  } else if (weather === 'dust' || weather === 'desert') {
    ctx.fillStyle = 'rgba(230,180,120,0.35)';
    for (const p of ambient) ctx.fillRect(p.x, p.y, p.s, p.s);
  } else if (weather === 'neon') {
    ctx.fillStyle = 'rgba(255,80,190,0.55)';
    for (const p of ambient) { ctx.beginPath(); ctx.arc(p.x, p.y, p.s, 0, Math.PI * 2); ctx.fill(); }
  }
}

function drawAurora(ctx, W, midY, phase, deep) {
  const bands = [
    { color: '124,231,193', dy: -30, amp: 22, freq: 0.010 },
    { color: '143,184,255', dy: 0,   amp: 30, freq: 0.008 },
    { color: '189,140,230', dy: 30,  amp: 20, freq: 0.012 },
  ];
  ctx.globalCompositeOperation = 'lighter';
  for (const b of bands) {
    ctx.beginPath();
    for (let x = 0; x <= W; x += 8) {
      const y = midY + b.dy + Math.sin(x * b.freq + phase + b.dy) * b.amp;
      if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.lineTo(W, midY + b.dy + 60);
    ctx.lineTo(0, midY + b.dy + 60);
    ctx.closePath();
    const alpha = deep ? 0.28 : 0.22;
    const grad = ctx.createLinearGradient(0, midY + b.dy - 30, 0, midY + b.dy + 60);
    grad.addColorStop(0, `rgba(${b.color},${alpha})`);
    grad.addColorStop(1, `rgba(${b.color},0)`);
    ctx.fillStyle = grad;
    ctx.fill();
  }
  ctx.globalCompositeOperation = 'source-over';
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
